/* eslint-disable @typescript-eslint/naming-convention */
import { isError, toString } from 'lodash-es'
import { isGraphqlError, toError } from '~/graphql'

import { GraphQLAuthError } from '@aws-amplify/api-graphql'
import { QueryClient } from '@tanstack/react-query'

// 認証エラー時に再認証中かどうか
let isRetryAuthInProgress = false

// GraphQL 認証エラー（IDトークンの有効期限が切れた場合など）
const GraphQLUnauthorizedError = 'Request failed with status code 401'
// CognitUserPool 初期化エラー
const CognitoUserPoolError = 'Username and Pool information are required.'

/**
 * QueryClient のリトライ処理
 *
 * GraphQL認証エラーが発生した際に、再認証してリトライします。
 */
const retry = (failureCount: number, error: unknown) => {
  const err = toError(error) ?? (isError(error) ? error : new Error(toString(error)))

  if (
    [GraphQLUnauthorizedError, GraphQLAuthError.NO_CURRENT_USER, GraphQLAuthError.NO_CREDENTIALS].includes(err.message)
  ) {
    if (isRetryAuthInProgress) {
      return true
    }

    isRetryAuthInProgress = true

    return true
  }

  if (isGraphqlError(error)) {
    return false
  }

  if (CognitoUserPoolError === err.message) {
    return false
  }

  console.error(`[QueryClient] retry=${failureCount}`, { error })

  return failureCount < 3
}

/**
 * QueryClient のリトライ遅延（ミリ秒）
 *
 * GraphQL認証エラーが発生した際に、固定秒待機します。
 * その他のエラーは、指数バックオフで待機します。
 */
const retryDelay = (failureCount: number, error: unknown) => {
  if (
    isError(error) &&
    (error.message === GraphQLAuthError.NO_CURRENT_USER || error.message === GraphQLAuthError.NO_CREDENTIALS)
  ) {
    return 3000
  }

  return Math.min(1000 * 2 ** (failureCount + 1), 30000)
}

/**
 * カスタム QueryClient を使用します
 */
export const createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry,
        retryDelay,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
      },
      mutations: { retry, retryDelay },
    },
  })
}
